/**
 * @fileoverview O passo 3 do assistente da IA: o momento em que a pessoa vê
 * que a IA aprendeu sobre o negócio. Conquista (o título com o nome do
 * negócio e uma comemoração discreta, só na primeira vez), descoberta (o que
 * ela aprendeu, em categorias, só o que foi achado, e o que faltou para
 * completar), primeiro teste (ela se apresenta e sugere perguntas tiradas da
 * base; a pergunta abre a conversa de teste já respondendo) e ativação.
 */

import React, { useEffect, useState } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  ArrowRight, BadgeCheck, Building2, Check, Clock, CreditCard, FilePlus2, FileText, Info, MapPin, MessageSquare, Package, Phone,
  Power, ShieldCheck, Tag, Truck,
} from "lucide-react";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { useAuthFormStyles } from "../AuthLayout/formStyles";
import { useAiSetupStyles } from "./styles";

const HIGHLIGHT_ICONS = { hours: Clock, locations: MapPin, contact: Phone, payment: CreditCard, delivery: Truck, warranty: ShieldCheck, other: Info };
const BURST_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];
const FALLBACK_QUESTIONS = ["howItWorks", "services", "hours"];

export const questionsOf = (profile) =>
  profile?.questions?.length ? profile.questions : FALLBACK_QUESTIONS.map((key) => i18n.t(`aiSetup.test.suggestions.${key}`));

// Com o nome padrão ("Assistente"), as frases não repetem "Assistente, assistente".
export const isDefaultName = (name) => !name || name.trim().toLowerCase() === i18n.t("aiSetup.learned.defaultName").toLowerCase();

export const greetingOf = (name, businessName, profile) =>
  [
    isDefaultName(name) ? i18n.t("aiSetup.meet.helloDefault", { business: businessName }) : i18n.t("aiSetup.meet.hello", { name, business: businessName }),
    profile?.greeting || i18n.t("aiSetup.meet.helpFallback"),
  ].join(" ");

export const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("") || "IA";

const listText = (items) => {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} ${i18n.t("aiSetup.learned.and")} ${items[items.length - 1]}`;
};

const LearnedStep = ({
  head,
  result,
  businessName,
  missing,
  celebrate,
  agentReady,
  agentName,
  onNameChange,
  onNameCommit,
  saving,
  actionError,
  onRetrySave,
  canActivate,
  onActivate,
  onSkipActivation,
  onOpenBase,
  onAsk,
}) => {
  const classes = useAiSetupStyles();
  const form = useAuthFormStyles();
  const profile = result.profile;
  const [whatsappConnected, setWhatsappConnected] = useState(null);

  // O que acontece ao ativar depende de o WhatsApp já estar conectado.
  useEffect(() => {
    let alive = true;
    api
      .get("/whatsapp/")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data?.whatsapps || [];
        if (alive) setWhatsappConnected(list.some((w) => w.status === "CONNECTED" && w.provider !== "livechat"));
      })
      .catch(() => alive && setWhatsappConnected(null));
    return () => {
      alive = false;
    };
  }, []);

  const name = agentName.trim() || i18n.t("aiSetup.learned.defaultName");
  const enter = celebrate ? classes.findingEnter : "";
  const findings = [];
  if (profile?.summary || result.about) {
    findings.push({ key: "about", Icon: Building2, body: <p className={classes.findingText}>{profile?.summary || result.about}</p> });
  }
  if (profile?.services?.length) {
    findings.push({
      key: "services",
      Icon: Package,
      body: <div className={classes.chipRow}>{profile.services.map((item) => <span key={item} className={classes.tag}>{item}</span>)}</div>,
    });
  } else if (!profile && result.offerings) {
    findings.push({ key: "services", Icon: Package, body: <p className={classes.findingText}>{result.offerings}</p> });
  }
  if (profile?.brands?.length) {
    findings.push({
      key: "brands",
      Icon: Tag,
      body: <div className={classes.chipRow}>{profile.brands.map((item) => <span key={item} className={classes.tag}>{item}</span>)}</div>,
    });
  }
  if (profile?.highlights?.length) {
    findings.push({
      key: "highlights",
      Icon: Info,
      body: (
        <ul className={classes.factList}>
          {profile.highlights.map(({ kind, text }) => {
            const Icon = HIGHLIGHT_ICONS[kind] || Info;
            return (
              <li key={`${kind}-${text}`} className={classes.fact}>
                <Icon size={14} /> {text}
              </li>
            );
          })}
        </ul>
      ),
    });
  }

  return (
    <div className={`${classes.root} ${classes.view}`}>
      {head}

      {/* Conquista */}
      <div className={classes.hero}>
        <span className={`${classes.heroIcon}${celebrate ? ` ${classes.heroIconCelebrate}` : ""}`} aria-hidden="true">
          <BadgeCheck size={28} />
          {celebrate && BURST_ANGLES.map((angle) => <span key={angle} className={classes.burst} style={{ "--angle": `${angle}deg` }} />)}
        </span>
        <div>
          <h1 className={classes.heroTitle}>{i18n.t("aiSetup.learned.heroTitle", { name: businessName })}</h1>
          <p className={classes.heroText}>{i18n.t("aiSetup.learned.heroText")}</p>
        </div>
      </div>

      <div className={classes.learnedLayout}>
        {/* Descoberta */}
        <section className={classes.card} aria-labelledby="ai-learned-title">
          <div className={classes.cardHead}>
            <h2 id="ai-learned-title" className={classes.cardTitle} style={{ margin: 0 }}>{i18n.t("aiSetup.learned.cardTitle")}</h2>
            <button type="button" className={classes.linkButton} onClick={() => onOpenBase()}>
              <FileText size={14} /> {i18n.t("aiSetup.learned.viewBase")}
            </button>
          </div>
          {findings.length > 0 ? (
            findings.map(({ key, Icon, body }, index) => (
              <div key={key} className={`${classes.finding} ${enter}`} style={celebrate ? { animationDelay: `${300 + index * 70}ms` } : undefined}>
                <span className={classes.findingIcon}><Icon size={16} /></span>
                <div>
                  <span className={classes.findingLabel}>{i18n.t(`aiSetup.learned.findings.${key}`)}</span>
                  {body}
                </div>
              </div>
            ))
          ) : (
            <p className={classes.learnedEmpty}>{i18n.t("aiSetup.learned.nothingFound")}</p>
          )}
          {missing.length > 0 && (
            <div className={classes.gap}>
              <FilePlus2 size={16} />
              <div>
                {i18n.t(`aiSetup.learned.missing.${result.source}`, { items: listText(missing.map((key) => i18n.t(`aiSetup.learned.missingItems.${key}`))) })}
                <div className={classes.gapAction}>
                  <button type="button" className={form.link} onClick={() => onOpenBase(missing[0])}>
                    {i18n.t("aiSetup.learned.complete")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        {/* Primeiro teste */}
        <section className={classes.meet} aria-labelledby="ai-meet-title">
          <h2 id="ai-meet-title" className={classes.meetTitle}>{i18n.t("aiSetup.meet.title")}</h2>
          <div className={classes.nameRow}>
            <label className={classes.nameLabel} htmlFor="ai-setup-name">{i18n.t("aiSetup.learned.nameLabel")}</label>
            <input
              id="ai-setup-name"
              className={`${form.input} ${classes.nameInput}`}
              value={agentName}
              maxLength={60}
              onChange={(event) => onNameChange(event.target.value)}
              onBlur={onNameCommit}
            />
          </div>
          <div className={classes.greetingRow}>
            <span className={classes.aiAvatar} aria-hidden="true">{initialsOf(name)}</span>
            <div className={classes.greeting}>{greetingOf(name, businessName, profile)}</div>
          </div>
          <span className={classes.askLabel}>{i18n.t("aiSetup.meet.ask")}</span>
          <div className={classes.questionList}>
            {questionsOf(profile).map((question) => (
              <button key={question} type="button" className={classes.question} onClick={() => onAsk(question)} disabled={!agentReady}>
                {question}
                <ArrowRight size={15} />
              </button>
            ))}
            <button type="button" className={classes.question} onClick={() => onAsk(null)} disabled={!agentReady}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                <MessageSquare size={15} /> {i18n.t("aiSetup.meet.free")}
              </span>
              <ArrowRight size={15} />
            </button>
          </div>
          {!agentReady && !actionError && (
            <div className={classes.preparingAgent}>
              <CircularProgress size={14} color="inherit" /> {i18n.t("aiSetup.meet.preparing")}
            </div>
          )}
        </section>
      </div>

      {actionError && (
        <div className={`${form.notice} ${form.noticeBad}`} style={{ maxWidth: 1040, marginTop: 18 }} role="alert">
          {actionError}
          {!agentReady && (
            <button type="button" className={form.link} style={{ marginLeft: 8 }} onClick={onRetrySave} disabled={saving}>
              {i18n.t("aiSetup.preparing.retry")}
            </button>
          )}
        </div>
      )}

      {/* Ativação */}
      <section className={classes.activation} aria-labelledby="ai-activate-title">
        <div>
          <h2 id="ai-activate-title" className={classes.activationTitle}>
            {isDefaultName(name) ? i18n.t("aiSetup.activate.titleDefault") : i18n.t("aiSetup.activate.title", { name })}
          </h2>
          <ul className={classes.activationList}>
            <li className={classes.activationItem}>
              <MessageSquare size={14} />
              {whatsappConnected === false ? i18n.t("aiSetup.activate.whenConnected") : i18n.t("aiSetup.activate.what")}
            </li>
            <li className={classes.activationItem}>
              <Power size={14} /> {i18n.t("aiSetup.activate.undo")}
            </li>
          </ul>
        </div>
        <div className={classes.activationActions}>
          {canActivate && (
            <button type="button" className={`${form.button} ${classes.activationPrimary}`} onClick={onActivate} disabled={saving}>
              {saving ? <CircularProgress size={18} className={form.spinner} /> : <Check size={18} />}
              {i18n.t("aiSetup.activate.cta")}
            </button>
          )}
          <button type="button" className={`${form.secondary} ${classes.activationSecondary}`} onClick={onSkipActivation} disabled={saving}>
            {i18n.t("aiSetup.activate.skip")}
          </button>
          <span className={classes.activationNote}>{i18n.t(canActivate ? "aiSetup.activate.saved" : "aiSetup.activate.savedAskEditor")}</span>
        </div>
      </section>
    </div>
  );
};

export default LearnedStep;
